import { ApiError, api } from '@/lib/api';
import {
  offlineDb,
  type QueuedMutation,
  type QueuedMutationMethod,
} from './db';

export interface EnqueueInput {
  method: QueuedMutationMethod;
  url: string;
  body?: unknown;
  headers?: Record<string, string>;
  mutationKey?: string;
}

/**
 * Append a new mutation to the queue. Always lands as `pending`.
 * Returns the row id so callers (e.g. an optimistic UI) can reference
 * the queued item.
 */
export async function enqueueMutation(input: EnqueueInput): Promise<number> {
  const row: QueuedMutation = {
    createdAt: new Date().toISOString(),
    status: 'pending',
    method: input.method,
    url: input.url,
    body: input.body,
    headers: input.headers,
    mutationKey: input.mutationKey,
    retries: 0,
  };
  return offlineDb.mutations.add(row);
}

/**
 * Synchronously pull the count of pending+replaying mutations. Useful
 * for one-shot reads (status badge already uses useLiveQuery for the
 * live count).
 */
export async function countQueued(): Promise<number> {
  return offlineDb.mutations
    .where('status')
    .anyOf('pending', 'replaying')
    .count();
}

/**
 * Drop a single mutation by id. Used by the manual replay UI in F11.4
 * when the operator decides a stale write is no longer relevant.
 */
export async function dropMutation(id: number): Promise<void> {
  await offlineDb.mutations.delete(id);
}

/**
 * Wipe the entire queue. Reserved for the user-confirmed "clear all"
 * action in F11.4.
 */
export async function clearAll(): Promise<void> {
  await offlineDb.mutations.clear();
}

/**
 * Replay one mutation against the API. Resolves to:
 *   - 'replayed' on 2xx — the row is deleted from the queue.
 *   - 'failed'   on a non-retryable 4xx (other than 408/409/429) —
 *                the row is marked failed with the server's message
 *                so the operator can inspect it.
 *   - 'retry'    on network / 408 / 429 / 5xx — the row goes back to
 *                pending with retries++, ready for the next pass.
 *
 * Callers don't usually invoke this directly; replayAll wraps it.
 */
async function replayOne(
  row: QueuedMutation,
): Promise<'replayed' | 'failed' | 'retry'> {
  if (row.id === undefined) return 'failed';
  await offlineDb.mutations.update(row.id, { status: 'replaying' });

  try {
    await api.request({
      method: row.method,
      url: row.url,
      data: row.body,
      headers: row.headers,
    });
    await offlineDb.mutations.delete(row.id);
    return 'replayed';
  } catch (err: unknown) {
    // The shared axios interceptor wraps every failure in ApiError,
    // which exposes statusCode rather than a raw `response.status`.
    // Fall back to the axios shape just in case the interceptor is
    // bypassed (network errors that throw before response).
    const status =
      err instanceof ApiError
        ? err.statusCode
        : err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
    const message =
      err instanceof Error ? err.message : 'Unknown replay error';

    // Network errors (no response) and the standard retry-able codes
    // go back to the pending queue with retries++. Other 4xx are
    // terminal so the user can decide what to do.
    const retryable =
      status === undefined ||
      status === 408 ||
      status === 409 ||
      status === 429 ||
      (status >= 500 && status <= 599);

    if (retryable) {
      await offlineDb.mutations.update(row.id, {
        status: 'pending',
        retries: row.retries + 1,
        lastError: message,
      });
      return 'retry';
    }
    await offlineDb.mutations.update(row.id, {
      status: 'failed',
      retries: row.retries + 1,
      lastError: message,
    });
    return 'failed';
  }
}

export interface ReplayAllResult {
  replayed: number;
  failed: number;
  retried: number;
}

/**
 * Replay every pending mutation in chronological order. Stops on the
 * first network failure (a single offline tick fails fast) but keeps
 * going across non-retryable failures so each terminal row gets
 * surfaced individually.
 *
 * The serial order matters: a PATCH that depends on a prior POST
 * would 404 if we replayed in parallel.
 */
export async function replayAll(): Promise<ReplayAllResult> {
  const pending = await offlineDb.mutations
    .where('status')
    .equals('pending')
    .sortBy('createdAt');

  const result: ReplayAllResult = {
    replayed: 0,
    failed: 0,
    retried: 0,
  };

  for (const row of pending) {
    const outcome = await replayOne(row);
    if (outcome === 'replayed') result.replayed += 1;
    else if (outcome === 'failed') result.failed += 1;
    else {
      result.retried += 1;
      // A single network blip means we're offline again — bail out.
      break;
    }
  }
  return result;
}
